import React from 'react';
import type { ReturnableBox } from '../../lib/debitNoteApi';
import type { ReturnSource, ReturnSourceLine, ReturnSources } from '../../lib/creditNoteApi';
import { ErrorState } from '../../components/ui/States';
import BoxPicker from '../debitNotes/BoxPicker';
import { formatDate, storedDay } from '../../utils/dateFormat';
import {
  EMPTY_SPLIT,
  RowSplit,
  isAutoFilled,
  noSourcesMessage,
  quantityTextOf,
  returnCheckOf,
  sourceLineOf,
  tickedBoxesOf,
  tickedWeightOf,
} from './stockReturnDraft';

/** One credit note row as the split needs it. */
export type SplitRow = {
  /** The row's place in the form, which is how a change finds it again. */
  index: number;
  /** The invoice line the row credits; null for a row typed in by hand. */
  lineNumber: number | null;
  description: string;
  uom: string;
  /** Positive. */
  credited: number;
  split: RowSplit | undefined;
};

function withUnit(value: number, unit: string): string {
  return `${value} ${unit || ''}`.trim();
}

/** A box on a shipment, in the shape the vendor return's picker reads: every box listed can go back. */
function pickerBoxesOf(source: ReturnSource): ReturnableBox[] {
  return (source.boxes || []).map((box) => ({
    seq: box.seq,
    code: box.code,
    weightLevel1: box.weightLevel1,
    weightRemainingLevel1: box.weightLevel1,
    status: 'available',
    heldByThis: false,
    available: true,
    quantity: box.weightLevel1,
  }));
}

/**
 * "Back into United stock" on a DRAFT Item Return (D44 #8, #35-#38, #42, VR-4):
 * for each credit note row, the shipments its invoice line went out on and the
 * batches they drew from, and how much goes back into each. The person splits the
 * credited quantity across them (#36), ticks weighed boxes (#37), and types the
 * batch's own unit when the client's does not convert (#42).
 *
 * Every figure shown is the server's. The only ones worked out here are the
 * filled-in batch quantity and the credited-versus-going-back check, both
 * quantities; the server checks both again on Issue.
 */
export const StockReturnSplit: React.FC<{
  rows: SplitRow[];
  sources: ReturnSources | null;
  loading: boolean;
  error: string | null;
  /** No source invoice picked yet: there is nothing to split against. */
  hasInvoice: boolean;
  vesselName: string;
  /** Invoice line numbers the last refusal named. */
  flaggedLines: number[];
  onChange: (index: number, next: RowSplit) => void;
}> = ({ rows, sources, loading, error, hasInvoice, vesselName, flaggedLines, onChange }) => {
  if (!hasInvoice) {
    return (
      <p className="text-xs text-slate-500">Pick the source invoice above to say which batches the goods go back into.</p>
    );
  }

  const vessel = sources?.vesselName || vesselName;

  const setQuantity = (row: SplitRow, source: ReturnSource, text: string) => {
    const split = row.split ?? EMPTY_SPLIT;
    const prev = split.entries[source.key] ?? { quantity: '', caseSeqs: [] };
    onChange(row.index, {
      ...split,
      handEdited: true,
      entries: { ...split.entries, [source.key]: { ...prev, quantity: text } },
    });
  };

  const toggleBox = (row: SplitRow, source: ReturnSource, seq: number, ticked: boolean) => {
    const split = row.split ?? EMPTY_SPLIT;
    const prev = split.entries[source.key] ?? { quantity: '', caseSeqs: [] };
    const others = prev.caseSeqs.filter((s) => s !== seq);
    onChange(row.index, {
      ...split,
      entries: {
        ...split.entries,
        [source.key]: { ...prev, caseSeqs: (ticked ? [...others, seq] : others).sort((a, b) => a - b) },
      },
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Each line&apos;s goods go back into the batches they were shipped from. Say how many go back into each, in the
        batch&apos;s own unit, or tick the boxes that came back. Split the credited quantity when it went out from more
        than one batch.
      </p>
      {loading && <div className="text-xs text-slate-500">Reading where this invoice&apos;s goods went…</div>}
      {error && <ErrorState message={error} />}
      {rows.length === 0 && <div className="text-xs text-slate-500">Add the lines being returned first.</div>}

      {rows.map((row) => {
        const flagged = row.lineNumber !== null && flaggedLines.includes(row.lineNumber);
        const line: ReturnSourceLine | null = sourceLineOf(sources, row.lineNumber);
        const check = returnCheckOf(row.split, line, row.credited);
        const title =
          row.lineNumber !== null
            ? `Line ${row.lineNumber}${row.description ? `: ${row.description}` : ''}`
            : row.description || 'Row without an invoice line';
        return (
          <div
            key={row.index}
            data-testid={`stock-return-row-${row.index}`}
            data-flagged={flagged ? 'true' : undefined}
            className={`rounded-lg border p-3 ${flagged ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
          >
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <div className="text-xs font-semibold text-slate-700">{title}</div>
              <div className="text-xs text-slate-500">{withUnit(row.credited, row.uom)} credited</div>
            </div>

            {row.lineNumber === null ? (
              <div className="text-xs text-amber-700">
                This row is not tied to a line of the invoice, so its goods cannot go back into a batch.
              </div>
            ) : !line ? null : line.sources.length === 0 ? (
              <div className="text-xs text-amber-700" data-testid={`stock-return-no-sources-${row.index}`}>
                {noSourcesMessage(vessel)}
              </div>
            ) : (
              <>
                {/* Wide, so it scrolls inside its card on a phone. Dense: typed in bulk. */}
                <div className="overflow-x-auto">
                  <table className="data-table data-table-dense min-w-[900px] bg-white">
                    <thead>
                      <tr>
                        <th>Shipment</th>
                        <th>Batch</th>
                        <th className="text-right">Went out</th>
                        <th>Already back</th>
                        <th className="text-right">Can still go back</th>
                        <th className="w-48">Going back</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.sources.map((source, sourceIndex) => {
                        const subject =
                          line.sources.length > 1
                            ? `line ${row.lineNumber}, batch ${sourceIndex + 1}`
                            : `line ${row.lineNumber}`;
                        const text = quantityTextOf(row.split ?? EMPTY_SPLIT, source, line, row.credited);
                        const typed = Number(text);
                        const over = text.trim() !== '' && Number.isFinite(typed) && typed > source.returnableLevel1;
                        const auto = isAutoFilled(row.split ?? EMPTY_SPLIT, source, line);
                        return (
                          <tr key={source.key}>
                            <td className="align-top">
                              <div className="text-slate-800">{source.shipmentLabel}</div>
                              {source.shippedAt && <div className="text-slate-400">{formatDate(source.shippedAt, '')}</div>}
                            </td>
                            <td className="align-top">
                              <div className="text-slate-800">{source.batchLabel}</div>
                              {source.expiryDate && (
                                <div className="text-slate-500">expires {formatDate(storedDay(source.expiryDate), '')}</div>
                              )}
                              {source.location === 'pantry' && (
                                <div className="mt-0.5 whitespace-normal text-amber-700">
                                  From the Pantry, whose count belongs to Yelo: these goods go into a new United batch at
                                  the Pantry batch&apos;s cost and expiry. No vendor return can be raised for them.
                                </div>
                              )}
                            </td>
                            <td className="align-top text-right tabular-nums whitespace-nowrap">
                              {withUnit(source.sentLevel1, source.unitLevel1)}
                            </td>
                            <td className="align-top">
                              {source.returnedLevel1 > 0 ? (
                                <>
                                  <div className="tabular-nums">{withUnit(source.returnedLevel1, source.unitLevel1)}</div>
                                  {source.returnedOn.length > 0 && (
                                    <div className="text-slate-500">on {source.returnedOn.join(', ')}</div>
                                  )}
                                </>
                              ) : (
                                '--'
                              )}
                            </td>
                            <td className="align-top text-right font-semibold tabular-nums whitespace-nowrap">
                              {withUnit(source.returnableLevel1, source.unitLevel1)}
                            </td>
                            <td className="align-top">
                              {source.boxes ? (
                                source.boxes.length === 0 ? (
                                  <div className="whitespace-normal text-slate-500">
                                    No box from this shipment can go back: none went whole, or all have come back.
                                  </div>
                                ) : (
                                  <BoxPicker
                                    lineNumber={row.lineNumber as number}
                                    subject={subject}
                                    unit={source.unitLevel1}
                                    boxes={pickerBoxesOf(source)}
                                    selected={tickedBoxesOf(row.split ?? EMPTY_SPLIT, source)}
                                    invalid={flagged}
                                    quantity={tickedWeightOf(row.split ?? EMPTY_SPLIT, source)}
                                    onToggle={(seq, ticked) => toggleBox(row, source, seq, ticked)}
                                  />
                                )
                              ) : (
                                <>
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="number"
                                      min="0"
                                      step="any"
                                      max={source.returnableLevel1}
                                      aria-label={`Quantity going back from ${subject}`}
                                      aria-invalid={over || flagged || undefined}
                                      className={`w-full h-8 rounded-md border px-2 text-xs text-right ${
                                        over || flagged ? 'border-red-300' : 'border-slate-200'
                                      }`}
                                      value={text}
                                      placeholder={`max ${source.returnableLevel1}`}
                                      onChange={(e) => setQuantity(row, source, e.target.value)}
                                    />
                                    <span className="text-slate-500">{source.unitLevel1}</span>
                                  </div>
                                  {over ? (
                                    <div className="mt-0.5 text-red-700">
                                      max {withUnit(source.returnableLevel1, source.unitLevel1)}
                                    </div>
                                  ) : auto ? (
                                    <div className="mt-0.5 text-slate-400">filled in from the credited quantity</div>
                                  ) : null}
                                  {!source.level1PerClientUnit && (
                                    <div className="mt-0.5 whitespace-normal text-slate-500">
                                      {row.uom || 'The invoice unit'} does not convert to {source.unitLevel1 || 'this batch’s unit'}{' '}
                                      here: type how many {source.unitLevel1 || 'units'} go back.
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {check && check.goingBack !== null && (
                  <div
                    data-testid={`stock-return-check-${row.index}`}
                    className={`mt-2 text-xs ${check.mismatch ? 'font-medium text-red-700' : 'text-slate-600'}`}
                  >
                    {withUnit(check.credited, row.uom)} credited · {withUnit(check.goingBack, row.uom)} going back
                    {check.mismatch ? '. They must be the same.' : ''}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StockReturnSplit;
